import { ExpandLess, ExpandMore, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import { useGetToken } from '@vxui/common';
import { ComponentClass, FC, FunctionComponent, PropsWithChildren, useCallback, useState } from 'react';
import { Link, LinkProps, matchPath, resolvePath, useLocation, useMatch } from 'react-router-dom';

export type NavigationItemProps = {
  label: string;
  Icon: ComponentClass | FunctionComponent;
  to: string;
  reloadDocument?: boolean;
  state?: unknown;
  defaultOpen?: boolean;
  external?: boolean;
  addToken?: boolean;
};

const NavigationItemRoot = styled(Box)(({ theme }) => ({
  position: 'relative',
  color: theme.palette.primary[30],
  fontSize: '16px',
  lineHeight: '150%',
  fontWeight: 400,
  '&.NavigationItem-active .NavigationItem-button': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.light,
    boxShadow: `inset 0px -1px 0px ${theme.palette.secondary.main}`,
  },
  '& > a': { color: 'inherit' },
  '& .NavigationItem-button': {
    padding: '10px 20px',
    paddingRight: '10px;',
    backgroundColor: theme.palette.primary.main,
    gap: '10px',
  },
  '& .MuiListItemButton-root:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '& .MuiListItemButton-root .MuiListItemIcon-root': {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    color: 'inherit',
  },
  '& .MuiListItemButton-root .MuiListItemText-root': {
    margin: 0,
  },
}));

const MockedLink: FC<LinkProps> = ({ children }) => <>{children}</>;

export const NavigationItem: FC<PropsWithChildren<NavigationItemProps>> = ({
  label,
  Icon,
  to,
  defaultOpen = true,
  reloadDocument = false,
  children = [],
  state,
  external = false,
  addToken = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [href, setHref] = useState(to);
  const handleClick = () => {
    setOpen(!open);
  };
  const subitems = Array.isArray(children) && children?.length > 0 ? children.filter(Boolean) : [];
  const hasSubitems = subitems.length > 0;
  const location = useLocation();
  const getToken = useGetToken();

  let active = !!useMatch(to);
  if (hasSubitems) {
    active = subitems.reduce((isSubitemMatching, { props: { to } }) => {
      const { pathname } = resolvePath(to);
      const match = matchPath({ path: pathname, end: false }, location.pathname);
      return isSubitemMatching || !!match;
    }, false);
  }
  const LinkComponent = hasSubitems ? MockedLink : Link;

  const button = (
    <ListItemButton data-cy={`navigationItem_${to}`} className="NavigationItem-button" onClick={handleClick}>
      <Tooltip title={label} enterDelay={200}>
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
      </Tooltip>
      <ListItemText primary={label} />
      {hasSubitems && (
        <>
          {open ? <ExpandLess /> : <ExpandMore />}
          <KeyboardArrowRight sx={{ fontSize: '14px' }} />
        </>
      )}
    </ListItemButton>
  );

  const updateHrefWithToken = useCallback(() => {
    const token = getToken();
    const hasSearchParams = to.indexOf('?') > -1;
    const newHref = `${to}${token ? `${hasSearchParams ? '&' : '?'}token=${token}` : ''}`;
    setHref(newHref);
  }, [getToken, to]);

  return (
    <NavigationItemRoot
      className={`NavigationItem-root ${active ? 'NavigationItem-active' : ''}`}
      onMouseEnter={addToken ? updateHrefWithToken : undefined}
    >
      {external ? (
        <a href={href}>{button}</a>
      ) : (
        <LinkComponent to={to} reloadDocument={reloadDocument} state={state}>
          {button}
        </LinkComponent>
      )}
      {hasSubitems && (
        <Collapse in={open} timeout="auto">
          <List component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      )}
    </NavigationItemRoot>
  );
};
